import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Create mailto URL with form data
      const subject = encodeURIComponent(`[${formData.subject}] ${formData.name} - Contact Form`);
      const body = encodeURIComponent(
        `Name: ${formData.name}\n` +
        `Email: ${formData.email}\n` +
        `Subject: ${formData.subject}\n\n` +
        `Message:\n${formData.message}\n\n` +
        `---\n` +
        `Sent from PBX Pickle Contact Form`
      );

      const mailtoURL = `mailto:support@pbxpickle.com?subject=${subject}&body=${body}`;
      
      // Open default email client
      window.location.href = mailtoURL;
      
      // Reset form after a short delay
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setSubmitStatus('success');
        setIsSubmitting(false);
      }, 1000);
      
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block bg-lime-400/10 border border-lime-400/20 rounded-full px-6 py-3 mb-8">
              <span className="text-lime-400 font-bold text-sm uppercase tracking-wider">💬 CONTACT US</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-6 tracking-tight uppercase leading-none">
              GET IN <span className="text-lime-400">TOUCH</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Ready to join the PBX movement? Let's talk about bringing adaptive gear to your community.
            </p>
          </div>
        </div>
      </section>

      {/* Split Screen Layout */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            
            {/* Left Side - Image & Info */}
            <div className="space-y-8">
              {/* Coach Felix Quote Card */}
              <div className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border border-lime-400/20 rounded-3xl p-6 sm:p-8 mb-6 sm:mb-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-lime-400 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Mail className="w-10 h-10 text-black" />
                  </div>
                  <blockquote className="text-xl sm:text-2xl font-bold text-white mb-4 leading-relaxed">
                    "Every message helps us build a better community."
                  </blockquote>
                  <p className="text-lime-400 font-bold text-lg">— Coach Felix, Founder</p>
                </div>
              </div>

              {/* Contact Info Cards */}
              <div className="space-y-4 sm:space-y-6">
                <Card className="bg-gradient-to-br from-lime-400/5 to-purple-600/5 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300 group">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center space-x-4">
                      <div className="bg-lime-400 rounded-full p-3">
                        <Mail className="w-6 h-6 text-black" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">Email Us</h3>
                        <a href="mailto:support@pbxpickle.com" className="text-gray-300 hover:text-lime-400 transition-colors">
                          support@pbxpickle.com
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-lime-400/5 to-purple-600/5 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300 group">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center space-x-4">
                      <div className="bg-lime-400 rounded-full p-3">
                        <Phone className="w-6 h-6 text-black" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">Call Us</h3>
                        <a href="tel:+13127752359" className="text-gray-300 hover:text-lime-400 transition-colors">
                          (312) 775-2359
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-lime-400/5 to-purple-600/5 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300 group">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center space-x-4">
                      <div className="bg-lime-400 rounded-full p-3">
                        <MapPin className="w-6 h-6 text-black" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">Visit Us</h3>
                        <p className="text-gray-300">Des Plaines, Illinois 60016</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Right Side - Contact Form */}
            <div>
              <Card className="bg-gradient-to-br from-lime-400/5 to-purple-600/5 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300">
                <CardContent className="p-6 sm:p-8">
                  <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 uppercase tracking-tight">Send us a message</h2>
                  
                  {/* Success/Error Messages */}
                  {submitStatus === 'success' && (
                    <div className="mb-6 p-4 bg-lime-400/10 border border-lime-400/20 rounded-lg">
                      <p className="text-lime-400 font-medium">
                        ✅ Thank you! Your message has been prepared. Your email client should open shortly.
                      </p>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                      <p className="text-red-400 font-medium">
                        ❌ There was an issue opening your email client. Please send your message directly to support@pbxpickle.com
                      </p>
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-gray-300 font-medium mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:border-lime-400 focus:outline-none focus:ring-2 focus:ring-lime-400/20 transition-colors disabled:opacity-50"
                          placeholder="Your name"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-gray-300 font-medium mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:border-lime-400 focus:outline-none focus:ring-2 focus:ring-lime-400/20 transition-colors disabled:opacity-50"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-gray-300 font-medium mb-2">
                        Subject *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-lime-400 focus:outline-none focus:ring-2 focus:ring-lime-400/20 transition-colors disabled:opacity-50"
                      >
                        <option value="">Select a topic</option>
                        <option value="general">General Inquiry</option>
                        <option value="wholesale">Wholesale/Team Orders</option>
                        <option value="adaptive">Adaptive Gear Consultation</option>
                        <option value="ambassador">Ambassador Program</option>
                        <option value="collaboration">Collaboration</option>
                        <option value="support">Customer Support</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-gray-300 font-medium mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        rows={6}
                        className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:border-lime-400 focus:outline-none focus:ring-2 focus:ring-lime-400/20 transition-colors resize-none disabled:opacity-50"
                        placeholder="Tell us about your project, team, or how we can help..."
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-lime-400 text-black hover:bg-lime-300 font-black py-4 rounded-xl text-lg transition-colors duration-200 ease-out disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      {isSubmitting ? 'Opening Email Client...' : 'Send Message'}
                    </Button>
                  </form>

                  {/* Additional Info */}
                  <div className="mt-8 p-4 bg-lime-400/10 border border-lime-400/20 rounded-lg">
                    <p className="text-lime-400 font-medium text-sm">
                      💡 Looking for team orders or adaptive gear consultation? Mention it in your message and we'll connect you with Coach Felix directly.
                    </p>
                  </div>

                  {/* Direct Email Note */}
                  <div className="mt-4 p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
                    <p className="text-gray-300 text-sm">
                      📧 This form will open your default email client. You can also email us directly at{' '}
                      <a 
                        href="mailto:support@pbxpickle.com" 
                        className="text-lime-400 hover:text-lime-300 font-medium"
                      >
                        support@pbxpickle.com
                      </a>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Google Maps */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 sm:mb-16 text-center">
            <div className="inline-block bg-lime-400/10 border border-lime-400/20 rounded-full px-6 py-3 mb-8">
              <span className="text-lime-400 font-bold text-sm uppercase tracking-wider">📍 LOCATION</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-black text-white mb-4 uppercase tracking-tight leading-none">
              <span className="text-lime-400">FIND</span> US
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Located in the heart of Des Plaines, Illinois - Visit us or reach out anytime
            </p>
          </div>
          
          <div className="relative h-96 rounded-2xl overflow-hidden border border-lime-400/20 shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d93732.57899333!2d-87.90829429999999!3d42.033613699999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880fb14e22c68515%3A0xa3929be8e5b1bd00!2sDes%20Plaines%2C%20IL%2060016%2C%20USA!5e0!3m2!1sen!2sus!4v1703123456789!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="PBX Pickle Location - Des Plaines, Illinois"
              className="absolute inset-0"
            ></iframe>
            
            {/* Overlay with contact info */}
            <div className="absolute top-4 right-4 bg-black/90 backdrop-blur-sm border border-lime-400/20 rounded-xl p-4 max-w-xs">
              <h3 className="text-lime-400 font-bold text-sm mb-2 uppercase tracking-wide">PBX Pickle HQ</h3>
              <div className="space-y-1 text-xs text-gray-300">
                <div className="flex items-center">
                  <MapPin className="w-3 h-3 mr-2 text-lime-400" />
                  Des Plaines, IL 60016
                </div>
                <div className="flex items-center">
                  <Phone className="w-3 h-3 mr-2 text-lime-400" />
                  (312) 775-2359
                </div>
                <div className="flex items-center">
                  <Mail className="w-3 h-3 mr-2 text-lime-400" />
                  support@pbxpickle.com
                </div>
              </div>
            </div>
          </div>
          
          {/* Additional location info */}
          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              🚗 Easy access from I-294 and I-90 • 🚊 Near Des Plaines Metra Station • 🏢 Business hours: Mon-Fri 9AM-6PM CT
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
